import { useCallback, useMemo, useState } from "react";
import { flexRender } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import {
  FirestoreProductSubject,
  type KeywordFeedbackDetails,
} from "reedy-data/api";
import {
  TableRow,
  TableBody,
  TableHead,
  TableHeader,
  TableCell,
} from "components/table";
import { Skeleton } from "components/skeleton";
import { useTable } from "hooks/useTable";
import { useUrlSorting } from "hooks/useUrlSorting";
import { TableEmptyState } from "./TableEmptyState";
import { TableFooter } from "./TableFooter";
import { columns } from "./columns";
import { keyForKeyword } from "../../hooks/useKeywordFeedback";
import type { KeywordFeedbackEntry } from "../../hooks/useKeywordFeedback";

const SKELETON_CELL_KEYS = ["keyword", "database", "score"] as const;
const SKELETON_ROW_KEYS = ["sk-1", "sk-2", "sk-3", "sk-4", "sk-5"] as const;

const SkeletonRow = () => (
  <TableRow data-testid="skeleton-row">
    {SKELETON_CELL_KEYS.map((key) => (
      <TableCell key={key}>
        <Skeleton className="h-4 w-full" />
      </TableCell>
    ))}
  </TableRow>
);

export interface UnifiedKeywordsTableViewProps {
  data: {
    keywords: FirestoreProductSubject[];
    finalKeywordWords: string[];
    databaseKeywordsCount: number;
    hasGeneratedKeywords: boolean;
  };
  status: { isGenerating: boolean };
  flags: {
    showReasoning: boolean;
    showScorePlaceholder: boolean;
    showPipelineInfoButton: boolean;
  };
  isInFinal: (subject: FirestoreProductSubject) => boolean;
  onToggle: (subject: FirestoreProductSubject) => Promise<void> | void;
  onGenerate: () => Promise<void> | void;
  onPipelineInfoOpen?: () => void;
  getFeedbackEntry: (keyword: string) => KeywordFeedbackEntry | undefined;
  onSubmitFeedback: (
    keyword: string,
    details?: KeywordFeedbackDetails,
  ) => Promise<void>;
  onDiscardFeedback: (keyword: string) => Promise<void>;
}

export const UnifiedKeywordsTableView = ({
  data,
  status,
  flags,
  isInFinal,
  onToggle,
  onGenerate,
  onPipelineInfoOpen,
  getFeedbackEntry,
  onSubmitFeedback,
  onDiscardFeedback,
}: UnifiedKeywordsTableViewProps) => {
  const { t } = useTranslation(["components"]);
  const {
    keywords,
    finalKeywordWords,
    databaseKeywordsCount,
    hasGeneratedKeywords,
  } = data;
  const { isGenerating } = status;
  const { showReasoning, showScorePlaceholder, showPipelineInfoButton } = flags;

  const [openFeedbackKey, setOpenFeedbackKey] = useState<string | null>(null);

  const handleFeedbackOpenChange = useCallback(
    (keyword: string, open: boolean) => {
      const key = keyForKeyword(keyword);
      setOpenFeedbackKey((current) => {
        if (open) return key;
        return current === key ? null : current;
      });
    },
    [],
  );

  const isFeedbackOpen = useCallback(
    (keyword: string) => openFeedbackKey === keyForKeyword(keyword),
    [openFeedbackKey],
  );

  const tableColumns = useMemo(
    () =>
      columns(t, {
        isScoring: isGenerating,
        hasGeneratedKeywords,
        showScoring: true,
        showScorePlaceholder,
        showReasoning,
      }),
    [
      t,
      isGenerating,
      hasGeneratedKeywords,
      showScorePlaceholder,
      showReasoning,
    ],
  );
  const sortableColumnIds = useMemo(
    () =>
      tableColumns
        .filter((column) => column.enableSorting !== false)
        .map((column) => {
          if ("accessorKey" in column && typeof column.accessorKey === "string")
            return column.accessorKey;

          return column.id;
        })
        .filter((columnId): columnId is string => typeof columnId === "string"),
    [tableColumns],
  );
  const { sorting, setSorting } = useUrlSorting({
    validColumnIds: sortableColumnIds,
  });

  const { table } = useTable({
    columns: tableColumns,
    data: keywords,
    totalRecords: keywords.length,
    getRowId: (row, index) => row.id ?? `row-${index}`,
    enableSorting: true,
    enableMultiSort: false,
    sortDescFirst: false,
    sorting,
    setSorting,
  });

  const showEmptyState = keywords.length === 0 && !isGenerating;
  const showTableFooter = !hasGeneratedKeywords && databaseKeywordsCount > 0;

  if (showEmptyState && databaseKeywordsCount === 0) {
    return (
      <TableEmptyState onGenerate={onGenerate} isGenerating={isGenerating} />
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0 relative">
      <div className="flex-1 flex flex-col min-h-0 border border-secondary-200 rounded-xl overflow-hidden">
        <div
          className="flex-1 overflow-auto"
          data-keywords-scroll-parent="true"
        >
          <table className="w-full caption-bottom text-sm">
            <TableHeader className="sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{ width: header.getSize() }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="[&>tr:last-child]:border-b [&>tr:last-child]:border-secondary-100">
              {table.getRowModel().rows.map((row) => {
                return (
                  <TableRow
                    key={row.id}
                    className="group"
                    data-testid="keyword-row"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, {
                          ...cell.getContext(),
                          matchingWords: finalKeywordWords,
                          isInFinal,
                          onToggle: isGenerating ? undefined : onToggle,
                          getFeedbackEntry,
                          isFeedbackOpen,
                          onFeedbackOpenChange: handleFeedbackOpenChange,
                          onFeedbackSubmit: onSubmitFeedback,
                          onFeedbackDiscard: onDiscardFeedback,
                        })}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {table.getRowModel().rows.length === 0 &&
                isGenerating &&
                SKELETON_ROW_KEYS.map((key) => <SkeletonRow key={key} />)}

              {table.getRowModel().rows.length === 0 && !isGenerating && (
                <TableRow className="bg-secondary-25">
                  <TableCell
                    colSpan={tableColumns.length}
                    className="h-24 text-center"
                  >
                    {t("components:keywordsTab.unifiedKeywordsTable.noResults")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </table>
        </div>
        {showTableFooter && (
          <TableFooter
            showPipelineInfoButton={showPipelineInfoButton}
            onPipelineInfoOpen={onPipelineInfoOpen}
          />
        )}
      </div>
    </div>
  );
};
